import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Instagram, Youtube, Smartphone, ArrowUpRight } from 'lucide-react';

const LINKS = {
  Product: [
    { label: 'Features',     to: '/features' },
    { label: 'Pricing',      to: '/pricing' },
    { label: 'Use cases',    to: '/use-cases' },
    { label: 'Download app', href: '/waslx.apk', download: true },
  ],
  Company: [
    { label: 'About us', to: '/about' },
    { label: 'Contact',  to: '/contact' },
    { label: 'Blog',     to: '/blog' },
    { label: 'Careers',  to: '/careers' },
  ],
  Support: [
    { label: 'Help center',   to: '/help' },
    { label: 'Documentation', to: '/documentation' },
    { label: 'System status', to: '/status' },
    { label: 'API reference', to: '/api-reference' },
  ],
};

const LEGAL = [
  { label: 'Privacy policy',   to: '/privacy' },
  { label: 'Terms of service', to: '/terms' },
  { label: 'Cookie policy',    to: '/cookie-policy' },
];

/* Each network's own brand hex, handed to .social-tile as --brand. Kept
   here rather than in the stylesheet so a new network is one line. */
const SOCIAL = [
  { Icon: Linkedin,  href: 'https://linkedin.com/company/waslx', label: 'LinkedIn',  brand: '#0A66C2' },
  { Icon: Twitter,   href: 'https://twitter.com/waslx',          label: 'Twitter',   brand: '#1D9BF0' },
  { Icon: Instagram, href: 'https://instagram.com/waslx.app',    label: 'Instagram', brand: '#E4405F' },
  { Icon: Youtube,   href: 'https://youtube.com/@waslx',         label: 'YouTube',   brand: '#FF0000' },
];

/* One hue per link column. Blue / violet / cyan are far enough apart to
   scan and none of them is the green the CTA above owns. */
const GROUP_ACCENT = {
  Product: 'accent-1',
  Company: 'accent-5',
  Support: 'accent-6',
};

const linkCls =
  'text-[0.9375rem] text-content-muted transition-colors hover:text-content';

/**
 * The footer is the page's quiet close: --color-surface with a hairline,
 * under a closing panel that is doing all the talking. It spent a version on
 * the dark nav plane, which read well on its own and badly in place — the
 * offer card, the banner and the footer stacked three deep surfaces in a row
 * and the bottom third of the page became one mass. One dark anchor per page
 * is the rule that came out of it.
 *
 * The surfaces inside it are --color-well tiles rather than white-on-white:
 * on a white ground the download and the social row need a ground of their
 * own, and the hairline does the separating that a shadow used to.
 *
 * The social row moved up under the brand blurb, which leaves the bottom bar
 * as what it should be: a copyright and the legal links, on one line.
 */
const Footer = () => (
  <footer className="border-t border-line bg-surface">
    <div className="container-page py-16">
      <div className="mb-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:gap-12">
        <div>
          <Link to="/" className="focus-ring mb-5 inline-block rounded-sm" aria-label="WASLX home">
            <img src="/logo-loop.webp" alt="WASLX" className="logo-anim h-12 w-auto" />
          </Link>

          <p className="mb-6 max-w-xs text-[0.9375rem] leading-relaxed text-content-muted">
            Mobile-first call center platform for modern teams. Run your entire operation from an
            Android smartphone.
          </p>

          {/* The one action in the footer, so it gets a surface of its own —
              Android's own green, at full strength. Text colour comes from
              .footer-apk rather than the --color-text tokens: this panel is
              a fixed brand hue, so the theme's ink would fight it. */}
          <a
            href="/waslx.apk"
            download
            className="focus-ring footer-apk accent-brand group inline-flex items-center gap-3 rounded-md shadow-md px-4 py-2.5 text-left"
          >
            <span className="icon-tile icon-tile--sm">
              <Smartphone className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="footer-apk-label block text-2xs uppercase tracking-[0.1em]">
                Download APK
              </span>
              <span className="block text-[0.9375rem] font-bold">Android app</span>
            </span>
            <ArrowUpRight className="h-4 w-4 flex-shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>

          <div className="mt-8 flex items-center gap-2">
            {SOCIAL.map(({ Icon, href, label, brand }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                style={{ '--brand': brand }}
                className="focus-ring social-tile"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {Object.entries(LINKS).map(([group, links]) => (
          <div key={group} className="accent-6">
            <h4 className="footer-heading mb-4 text-2xs font-bold uppercase tracking-[0.12em]">
              {group}
            </h4>
            <ul className="space-y-2.5">
              {links.map(link => (
                <li key={link.label}>
                  {link.href ? (
                    <a href={link.href} download={link.download || undefined} className={linkCls}>
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.to} className={linkCls}>
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex flex-col items-center justify-between gap-4 border-t border-line pt-8 sm:flex-row">
        {/* Stamped at render rather than typed: a hardcoded year is wrong
            every January, and this one was already a year behind. */}
        <p className="text-sm text-content-muted">
          © {new Date().getFullYear()} WASLX. All rights reserved.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {LEGAL.map(item => (
            <Link
              key={item.label}
              to={item.to}
              className="text-sm text-content-muted transition-colors hover:text-content"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
